import type { Tracklist } from "@api/tracklists/types";
import Button from "@components/Button";
import { Row } from "@components/Layout";
import useI18n from "@contexts/I18nProvider/hooks/useI18n";
import { themeContext } from "@contexts/Theme";
import { googleContext } from "@layouts/App/context/Google";
import { appThemes } from "@src/app/styles/themes";
import { Show, type VoidComponent, createEffect, createSignal, on, useContext } from "solid-js";
import { createStore } from "solid-js/store";
import ProgrammeCard from "./components/ProgrammeCard";
import Search from "./components/Search";
import { MapWrapper } from "./styled";
import { getDistanceFromLatLonInM } from "./utils";

const TracklistMap: VoidComponent<{
	mapLibrary: google.maps.MapsLibrary;
	onDrag?: (radius: { center: { lat: number; lng: number }; radius: number }) => Promise<Tracklist[]>;
	tracklists?: Tracklist[];
	onLike?: (uuid: Tracklist["uuid"]) => void;
	onBookmark?: (uuid: Tracklist["uuid"]) => void;
	onSelect?: (lat: number, lng: number) => void;
}> = (props) => {
	let ref: HTMLDivElement | undefined;
	const [map, setMap] = createSignal<google.maps.Map>();
	const [defaultCenter, setDefaultCenter] = createStore({
		lat: 50.0745,
		lng: 14.4635,
	});
	const [myCenter, setMyCenter] = createSignal<{ lat: number; lng: number }>();
	const [showReset, setShowReset] = createSignal(false);
	const [activeTracklist, setActiveTracklists] = createSignal<Tracklist["uuid"]>();
	const [selectMarker, setSelectMarker] = createSignal<google.maps.marker.AdvancedMarkerElement>();
	const { t } = useI18n();
	const gLibs = useContext(googleContext);
	const { theme } = useContext(themeContext);

	const handleChangeLocation = (location: google.maps.LatLng) => {
		setDefaultCenter("lat", location.lat());
		setDefaultCenter("lng", location.lng());

		setShowReset(true);
		const m = map();
		if (!m) return;
		m.setCenter(defaultCenter);
		if (props.onSelect) handleSelect(location);
	};

	const getLocation = () => {
		const getCurrentLocation = () => {
			navigator.geolocation.getCurrentPosition((pos) => {
				const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
				setDefaultCenter(c);
				const m = map();
				if (!m) return;
				setMyCenter(c);
				m.setCenter(c);
				const pin = new google.maps.marker.PinElement({
					background: appThemes[theme()].colors.accentPrimary,
					borderColor: "#fff",
					glyphColor: "#fff",
					scale: 1.2,
				});
				new google.maps.marker.AdvancedMarkerElement({
					map: m,
					position: c,
					content: pin.element,
					title: t("app.location.you"),
				});
			});
		};
		try {
			getCurrentLocation();
		} catch {
			navigator.permissions.query({ name: "geolocation" }).then((res) => {
				if (res.state === "denied") return;
				getCurrentLocation();
			});
		}
	};

	const handleAddTracklists = (tracklists: Tracklist[]) => {
		const m = map();
		if (!m) return;
		for (const tr of tracklists) {
			if (!tr.location) return;

			const content = document.createElement("div");
			content.id = tr.uuid;
			content.classList.add("marker");
			const pin = new google.maps.marker.PinElement({});
			content.appendChild(pin.element);

			const marker = new google.maps.marker.AdvancedMarkerElement({
				map: m,
				position: {
					lat: tr.location.coordinates.latitude,
					lng: tr.location.coordinates.longitude,
				},
				content,
			});
			marker.addListener("click", () => {
				setActiveTracklists(tr.uuid);
				if (content.classList.contains("show")) {
					// content.classList.remove('show');
					marker.zIndex = null;
				} else {
					content.classList.add("show");
					marker.zIndex = 1;
				}
			});
		}
	};

	const handleDrag = () => {
		const m = map();
		if (!m) return;
		const southWest = m.getBounds()?.getSouthWest();
		const lat = southWest?.lat() ?? defaultCenter.lat;
		const lng = southWest?.lng() ?? defaultCenter.lng;
		const radius = getDistanceFromLatLonInM(lat, lng, defaultCenter.lat, defaultCenter.lng, {
			max: 15000,
			min: 100,
		});
		props.onDrag?.({ center: { lat, lng }, radius }).then(handleAddTracklists);
	};

	const handleSelect = (c: google.maps.LatLng) => {
		const m = map();
		if (!m) return;
		let marker = selectMarker();
		if (!marker) {
			const pin = new google.maps.marker.PinElement();
			marker = new google.maps.marker.AdvancedMarkerElement({
				map: m,
				position: c,
				content: pin.element,
				title: t("app.location.you"),
			});
		}
		marker.position = c;
		setSelectMarker(marker);
		props.onSelect?.(c.lat(), c.lng());
	};

	createEffect(
		on(
			() => ref,
			() => {
				if (!ref) return;
				const m = new props.mapLibrary.Map(ref, {
					center: defaultCenter,
					zoom: 11,
					streetViewControl: true,
					mapTypeControl: true,
					tilt: 0,
					noClear: true,
					mapId: "voxtox",
				});
				getLocation();
				m.addListener("click", (e: { latLng: google.maps.LatLng }) => {
					setActiveTracklists(undefined);
					if (!props.onSelect) return;
					handleSelect(e.latLng);
				});
				setMap(m);
				m.addListener("dragend", () => {
					if (props.onDrag) handleDrag();
					setShowReset(!!myCenter());
				});
				handleDrag();
				handleAddTracklists(props.tracklists ?? []);
			},
		),
	);
	const handleReset = () => {
		const c = myCenter();
		const m = map();
		if (!m || !c) return;
		m.setCenter(c);
		setShowReset(false);
	};

	return (
		<>
			<Show when={props.tracklists?.find((tr) => tr.uuid === activeTracklist())} keyed>
				{(tr) => (
					<ProgrammeCard
						tracklist={tr}
						onBookmark={() => props.onBookmark?.(tr.uuid)}
						onLike={() => props.onLike?.(tr.uuid)}
					/>
				)}
			</Show>
			<Show when={map()} keyed>
				{(m) => (
					<Show when={gLibs.places} keyed>
						{(places) => <Search map={m} places={places} onLocation={handleChangeLocation} />}
					</Show>
				)}
			</Show>

			<MapWrapper>
				<Row ref={ref} h="40dvh" />
				<Show when={showReset()}>
					<Row padding="15px 0" justifyContent="flex-end">
						<Button h="40px" onclick={handleReset} fontSize="15px">
							{t("app.location.back")}
						</Button>
					</Row>
				</Show>
			</MapWrapper>
		</>
	);
};
export default TracklistMap;
